import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { auth, firestore } from './firebase-config';
import { doc, getDoc, setDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import renderContent from './renderContent';
import homePageContent from './homePageContent';
import './HomePage.css';

// ── Auth Modal ────────────────────────────────────────────────────────────────
function AuthModal({ onClose }) {
  const { loginUser } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');

  const handleSignUp = async () => {
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const offensiveWords = ['shit', 'fuck', 'dick', 'nigger', 'cunt', 'bitch'];
    if (offensiveWords.some(w => username.toLowerCase().includes(w))) {
      setMessage('Username contains offensive language.'); return;
    }
    if (!usernameRegex.test(username)) {
      setMessage('Username can only contain letters, numbers, and underscores.'); return;
    }
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address.'); return;
    }
    try {
      const q = query(collection(firestore, 'users'), where('username', '==', username));
      if (!(await getDocs(q)).empty) {
        setMessage('Username already taken.'); return;
      }
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(firestore, 'users', user.uid), { uid: user.uid, username, email, role: 'User', buttonScore: 0 });
      setMessage('Sign-up successful! You can now log in.');
      setIsLogin(true);
    } catch (e) { setMessage(e.message); }
  };

  const handleLogin = async () => {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(firestore, 'users', user.uid));
      if (snap.exists()) {
        const d = snap.data();
        loginUser({ uid: user.uid, username: d.username, email: d.email, role: d.role });
        onClose();
      } else {
        setMessage('User data not found.');
      }
    } catch (e) { setMessage(e.message); }
  };

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose}>✕</button>
        <h2>{isLogin ? 'Log In' : 'Sign Up'}</h2>
        {!isLogin && (
          <input placeholder="Username" value={username}
            onChange={e => setUsername(e.target.value)} />
        )}
        <input type="email" placeholder="Email" value={email}
          onChange={e => setEmail(e.target.value)} />
        <input type="password" placeholder="Password" value={password}
          onChange={e => setPassword(e.target.value)} />
        <button className="auth-submit" onClick={isLogin ? handleLogin : handleSignUp}>
          {isLogin ? 'Log In' : 'Sign Up'}
        </button>
        <button className="auth-switch" onClick={() => { setIsLogin(!isLogin); setMessage(''); }}>
          {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Log In'}
        </button>
        {message && <p className="auth-message">{message}</p>}
      </div>
    </div>
  );
}

// ── Recent Posts ──────────────────────────────────────────────────────────────
function RecentPosts() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      const q = query(collection(firestore, 'savedclay_posts'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setPosts(snap.docs.slice(0, 3).map(d => ({ id: d.id, ...d.data() })));
    };
    fetch();
  }, []);

  const formatDate = (ts) => {
    if (!ts) return '';
    return ts.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getPreview = (html = '', max = 120) => {
    const div = document.createElement('div');
    // Remove heading elements before extracting text
    div.innerHTML = html.replace(/<h[1-6][^>]*>.*?<\/h[1-6]>/gi, '');
    return (div.textContent || '').trim().slice(0, max).trimEnd();
  };

  if (!posts.length) return null;

  return (
    <section className="recent-posts">
      <h2 className="section-heading">Recent Posts</h2>
      <div className="recent-posts-grid">
        {posts.map(post => (
          <Link to={`/post/${post.id}`} key={post.id} className="recent-card">
            {post.coverImage && (
              <img src={post.coverImage} alt={post.title} className="recent-card-img" />
            )}
            <div className="recent-card-body">
              <p className="recent-card-meta">{formatDate(post.createdAt)}</p>
              <h3 className="recent-card-title">{post.title}</h3>
              <p className="recent-card-preview">{getPreview(post.content)}…</p>
              <span className="recent-card-read">Read more →</span>
            </div>
          </Link>
        ))}
      </div>
      <div className="recent-posts-footer">
        <Link to="/blog" className="view-all-btn">View all posts →</Link>
      </div>
    </section>
  );
}

// ── HomePage ──────────────────────────────────────────────────────────────────
export default function HomePage() {
  const { loggedInUser, logoutUser } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);

  const handleLogout = async () => {
    await signOut(auth);
    logoutUser();
  };

  return (
    <div className="home-page">
      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-inner">
          {/* <p className="home-tagline">A personal study of scripture</p> */}
          <div className="home-auth-bar">
            {loggedInUser ? (
              <>
                <span className="home-welcome">Welcome, {loggedInUser.username}</span>
                <button className="home-auth-btn outline" onClick={handleLogout}>Log Out</button>
              </>
            ) : (
              <button className="home-auth-btn" onClick={() => setShowAuth(true)}>Log In / Sign Up</button>
            )}
          </div>
        </div>
      </section>

      <div className="home-content">
        {/* About */}
        <section className="home-about">
          <p>{renderContent(homePageContent.description)}</p>
          <div className="home-divider" />
          <div className="home-note">{renderContent(homePageContent.note)}</div>
        </section>

        {/* Recent posts */}
        <RecentPosts />
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
}
